import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
    Box,
    Typography,
    Theme,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SettlementsTableDataRow from '../PaymentsView/SettlementsView/SettlementsTableDataRow';
import { getDasbhboardSettlements } from '../../api/paymentsApi/settlementsApi';
import NoDataText from '../../components/NoDataText';
import TableContainer from '../../components/Table/TableContainer';
import { getDashboardSettlementsTableLabels } from '../../constants/tableLabels';
import usePagination from '../../hooks/usePagination';
import { ActiveSortField, FilterTypes, SettlementShort } from '../../types';
import { handleSortTableData } from '../../helpers/handleSortTableData';
import { makeStyles } from 'tss-react/mui';
import FilterCards from '../../components/FilterCards';
import { TRACK } from '../../analytics/catalog';
import theme from '../../theme';
import TableActions from '../../components/Table/TableActions';
import Loader from '../../components/Loader';

const useStyles = makeStyles()((theme: Theme) => ({
    tableLabel: {
        fontSize: '20px',
        fontWeight: 600,
        lineHeight: '120%',
        color: theme.palette.common.white,
        fontFamily: 'Poppins',
    },
    tableBox: {
        flex: 1,
        minWidth: 0,
        paddingBottom: theme.spacing(2),
    },
    accordion: {
        backgroundColor: 'transparent',
        marginTop: theme.spacing(1),
        boxShadow: 'none',
        overflow: 'hidden',
        fontFamily: 'Poppins',
        borderRadius: '8px',
    },
    accordionSummary: {
        backgroundColor: theme.palette.primary.light,
        minHeight: '56px',
        padding: '0 16px',
        fontFamily: 'Poppins',
        '& .MuiTypography-root': {
            fontWeight: 600,
            fontSize: '16px',
            color: theme.palette.common.white,
            fontFamily: 'Poppins',
        },
        flexDirection: 'row-reverse',
    },
    accordionDetails: {
        padding: theme.spacing(2),
        backgroundColor: theme.palette.primary.light,
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(3),
        fontFamily: 'Poppins',
    },
    tableContainer: {
        width: '99%',
        background: 'transparent',
        border: `1px solid ${theme.palette.secondary.dark}`,
        borderRadius: '8px',
        overflow: 'auto',
    },
}));

const SettlementsTable = () => {
    const { classes } = useStyles();
    const params = useParams();

    const {
        pageNumber,
        rowsPerPage,
        setTotalCount,
        setPageNumber,
    } = usePagination();

    const [settlementsList, setSettlementsList] = useState<SettlementShort[]>([]);
    const [isDataLoading, setIsDataLoading] = useState(true);

    const [filters, setFilters] = useState<FilterTypes>({
        clientId: '',
        defendantId: '',
        status: '',
        processingStatus: '',
    });

    const [activeSortField, setActiveSortField] =
        useState<ActiveSortField | null>(null);

    const [activeFilter, setActiveFilter] = useState<string>();
    const [awaitingReviewCount, setAwaitingReviewCount] = useState(0);
    const [processingCount, setProcessingCount] = useState(0);
    const [toBePaidCount, setToBePaidCount] = useState(0);
    const [changesRequestedCount, setChangesRequestedCount] = useState(0);
    const [completedCount, setCompletedCount] = useState(0);
    const [isAccordionOpen, setIsAccordionOpen] = useState(false);
    const [match, setMatch] = useState<string>();

    const hasParams = !!Object.entries(params).length;

    const hasApprovalFlow = localStorage.getItem('hasSettlementApprovalFlow') === 'true';

    const settlementFilters = [
        ...(hasApprovalFlow
            ? [
                {
                    status: 'Awaiting Review',
                    count: awaitingReviewCount,
                    color: '#0da8c9',
                    trackId: TRACK.dashboard.settlements.awaitingReview,
        },
                {
                    status: 'Ready to Pay',
                    count: toBePaidCount,
                    color: theme.palette.success.main,
                    trackId: TRACK.dashboard.settlements.readyToPay,
        },
            ]
            : []),
        {
            status: 'Payment in Process',
            count: processingCount,
            color: theme.palette.warning.main,
            trackId: TRACK.dashboard.settlements.paymentInProcess,
        },
        {
            status: 'Payment Completed',
            count: completedCount,
            color: theme.palette.success.main,
            trackId: TRACK.dashboard.settlements.paymentCompleted,
        },
        ...(hasApprovalFlow
            ? [
                {
                    status: 'Changes Requested',
                    count: changesRequestedCount,
                    color: theme.palette.error.main,
                    trackId: TRACK.dashboard.settlements.changesRequested,
        },
            ]
            : []),
    ];

    const sortableFields = {
        createdAt: {
            order: '',
            fieldName: 'createdAt',
        },
        paidAt: {
            order: '',
            fieldName: 'paidAt',
        },
        updatedAt: {
            order: '',
            fieldName: 'updatedAt',
        },
    };

    const getProcessingStatusFilter = (filter?: string) => {
        switch (filter) {
            case 'Awaiting Review':
                return 'AWAITING_APPROVAL';

            case 'Payment in Process':
                return 'APPROVED_PROCESSING';

            case 'Ready to Pay':
                return 'APPROVED_TO_BE_PAID';

            case 'Changes Requested':
                return 'CHANGES_REQUESTED';

            case 'Payment Completed':
                return 'COMPLETED';    

            default:
                return '';
        }
    };

    const handleFilterChange = (filter?: string) => {
        setIsAccordionOpen(true);
        setIsDataLoading(true);
        setSettlementsList([]);
        setPageNumber?.(1);

        setActiveFilter(filter);

        setFilters((prev) => ({
            ...prev,
            processingStatus: getProcessingStatusFilter(filter),
        }));
    };

    useEffect(() => {
        setIsDataLoading(true);

        getDasbhboardSettlements({
            page: pageNumber,
            size: rowsPerPage,
            count: pageNumber === 1,
            match,
            filters: { ...filters, ...(hasParams && params) },
            activeSortField,
        })
            .then((res) => {
                if (res.data) {
                    if (pageNumber === 1) {
                        setTotalCount?.(res.data.totalCount || 0);
                    }

                    setSettlementsList(res.data.resultSet || []);
                    setAwaitingReviewCount(
                        res.data.processingStatusCount?.awaitingApprovalCount || 0
                    );
                    setProcessingCount(
                        res.data.processingStatusCount?.approvedProcessingCount || 0
                    );
                    setToBePaidCount(
                        res.data.processingStatusCount?.approvedToBePaidCount || 0
                    );
                    setChangesRequestedCount(
                        res.data.processingStatusCount?.changesRequestedCount || 0
                    );
                    setCompletedCount(
                        res.data.processingStatusCount?.completedCount || 0
                    );
                }
            })
            .finally(() => {
                setIsDataLoading(false);
            });
    }, [
        pageNumber,
        rowsPerPage,
        filters,
        activeSortField,
        match,
        hasParams,
        params,
        setTotalCount,
    ]);

    return (
        <Box sx={{ marginBottom: '4px' }}>
            <Typography
                className={classes.tableLabel}
                sx={{ marginBottom: '12px' }}
            >
                Settlements
            </Typography>

            <FilterCards
                filters={settlementFilters}
                activeFilter={activeFilter}
                onFilterChange={handleFilterChange}
            />

            <Accordion
                className={classes.accordion}
                sx={{ marginBottom: '14px' }}
                expanded={isAccordionOpen}
                onChange={(_, expanded) => setIsAccordionOpen(expanded)}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
                    className={classes.accordionSummary}
                >
                    <Typography className={classes.tableLabel}>
                        Settlements
                    </Typography>
                </AccordionSummary>

                <AccordionDetails className={classes.accordionDetails}>
                    <Box className={classes.tableBox}>
                        <TableActions
                            hasParams={hasParams}
                            match={match}
                            setMatch={(value) => {
                                // Loading is set by the fetch effect. Setting it here would leave the
                                // loader stuck when TableActions repeats setMatch('') with no refetch.
                                setMatch(value);
                                setPageNumber?.(1);
                            }}
                        />

                        {isDataLoading ? (
                            <Box
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                                minHeight="240px"
                            >
                                <Loader colorType="warning" size={60} />
                            </Box>
                        ) : settlementsList.length === 0 ? (
                            <NoDataText marginTop="20px" />
                        ) : (
                            <TableContainer
                                headerLabels={getDashboardSettlementsTableLabels(
                                    localStorage.getItem(
                                        'showUserOnboardingDetails'
                                    ) === 'true',
                                    localStorage.getItem(
                                        'hasSettlementApprovalFlow'
                                    ) === 'true'
                                )}
                                sortableFields={sortableFields}
                                activeSortField={activeSortField}
                                setActiveSortField={setActiveSortField}
                                handleSortTableData={handleSortTableData}
                                dashboard
                            >
                                <SettlementsTableDataRow
                                    tableData={settlementsList}
                                    caseId={params.caseId}
                                    type="dashboard"
                                />
                            </TableContainer>
                        )}
                    </Box>
                </AccordionDetails>
            </Accordion>
        </Box>
    );
};

export default SettlementsTable;